import React, { useState, useRef, useEffect, useCallback } from "react";
import { Badge, Dropdown, Layout, Menu, MenuProps } from "antd";
import { useNavigate, useLocation } from "react-router-dom";
import { MenuItemType } from "types/menu.types";
import { menuItems, sidebarItems, sidebarItemsConst } from "constant";
import {
  Bell,
  CaretDown,
  SignOut,
  User,
  Warehouse,
} from "@phosphor-icons/react";
import { SubMenu, Breadcrumb, AnimationCommon } from "components/common";
import { AnimatePresence } from "framer-motion";
import { logo_cms } from "utils/importFile";
import useAuthContext from "stores/auth-context";
import { useNotificationDropdown } from "hooks/useNotificationDropdown";
import NotificationDropdown from "./NotificationDropdown";

const { Header, Content } = Layout;

const Navbar: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const settingRef = useRef<HTMLDivElement>(null);
  const menuItemRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

  const navigate = useNavigate();
  const location = useLocation();
  const { profile, onSignOut } = useAuthContext();

  const [activeMenuKey, setActiveMenuKey] = useState<string>("");
  const [hoveredMenuKey, setHoveredMenuKey] = useState<string | null>(null);
  const [hoveredSetting, setHoveredSetting] = useState<boolean>(false);
  const [submenuPosition, setSubmenuPosition] = useState<{
    left: number;
    width: number;
  } | null>(null);

  const {
    isNotificationOpen,
    setIsNotificationOpen,
    notificationList,
    unreadCount,
    hasMoreNotifications,
    isFetchingNotifications,
    isMarkingAllAsRead,
    handleOpenNotifications,
    handleClickNotification,
    handleLoadMoreNotifications,
    handleMarkAllAsRead,
  } = useNotificationDropdown();

  const handleClick = useCallback(
    (e: { key: string }): void => {
      setActiveMenuKey(e.key);

      const findPath = (items: MenuItemType[]): string | undefined => {
        for (const item of items) {
          if (item.key === e.key) {
            return item.path;
          }

          if (item.children?.length) {
            const childPath = findPath(item.children);
            if (
              childPath &&
              item.children.some((child) => child.key === e.key)
            ) {
              return childPath;
            }
          }
        }

        return undefined;
      };

      const path = findPath(menuItems);
      if (path) {
        navigate(path.replace(/:\w+/g, ""));
      }
    },
    [navigate],
  );

  const items: MenuProps["items"] = [
    {
      key: "1",
      label: "Đăng xuất",
      icon: <SignOut size={20} />,
      onClick: () => onSignOut(),
      style: { color: "red", backgroundColor: "transparent" },
    },
  ];

  const findActiveKey = useCallback(
    (itemsInput: MenuItemType[]): string => {
      let bestMatch = "";
      let bestMatchLength = 0;

      for (const item of itemsInput) {
        const currentPath = (item.path || "").replace(/:\w+/g, "");

        if (currentPath && location.pathname.startsWith(currentPath)) {
          if (currentPath.length > bestMatchLength) {
            bestMatch = item.key;
            bestMatchLength = currentPath.length;
          }
        }

        if (item.children?.length) {
          const childMatch = findActiveKey(item.children);
          if (childMatch) {
            return childMatch;
          }
        }
      }

      return bestMatch;
    },
    [location.pathname],
  );

  useEffect(() => {
    setActiveMenuKey(findActiveKey(menuItems));
  }, [findActiveKey]);

  const handleMenuMouseEnter = useCallback((key: string): void => {
    setHoveredMenuKey(key);

    const menuItemEl = menuItemRefs.current[key];
    const containerEl = containerRef.current;

    if (!menuItemEl || !containerEl) {
      return;
    }

    const menuRect = menuItemEl.getBoundingClientRect();
    const containerRect = containerEl.getBoundingClientRect();

    setSubmenuPosition({
      left: menuRect.left - containerRect.left - 8,
      width: menuRect.width,
    });
  }, []);

  const handleSettingMouseEnter = useCallback((): void => {
    if (isNotificationOpen) {
      return;
    }

    setHoveredSetting(true);
  }, [isNotificationOpen]);

  const handleMouseLeave = useCallback((): void => {
    setHoveredSetting(false);
  }, []);

  const handleMenuMouseLeave = useCallback((): void => {
    setHoveredMenuKey(null);
  }, []);

  const renderMenuItems = useCallback(
    (itemsInput: MenuItemType[]) =>
      itemsInput.flatMap((item) => {
        if (!item?.isHide) {
          const hasChildren = item.children && item.children.length > 0;

          return {
            key: item.key,
            label: (
              <div
                ref={(el: HTMLDivElement | null) => {
                  menuItemRefs.current[item.key] = el;
                }}
                onMouseEnter={() => handleMenuMouseEnter(item.key)}
              >
                <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                  {item.label}
                  {hasChildren && <CaretDown size={18} />}
                </span>
              </div>
            ),
            children: undefined,
          };
        }

        return [];
      }),
    [handleMenuMouseEnter],
  );

  const hoveredMenuItem = hoveredMenuKey
    ? (menuItems.find((item: MenuItemType) => item.key === hoveredMenuKey) ??
      null)
    : null;

  return (
    <div
      className="sticky top-0 z-50"
      ref={containerRef}
      onMouseLeave={handleMenuMouseLeave}
    >
      <Header
        style={{
          position: "sticky",
          top: 0,
          zIndex: 1,
          display: "flex",
          alignItems: "center",
        }}
        className="gap-[24px] bg-[var(--opacityTextColor)] px-[24px] py-[12px]"
      >
        <div className="hidden w-[150px] text-[20px] font-bold leading-[100%] text-[var(--textWhite)] lg:flex">
          <img src={logo_cms} alt="logo" />
        </div>

        <Menu
          theme="dark"
          mode="horizontal"
          onClick={handleClick}
          selectedKeys={[activeMenuKey]}
          items={renderMenuItems(menuItems)}
          className="header-menu flex-1 bg-[var(--opacityTextColor)] text-[16px] font-[400] transition-all ease-in-out !outline-none lg:max-w-[calc(100%-400px)]"
        />

        <div className="flex items-center justify-end gap-[8px]">
          <Dropdown
            popupRender={() => (
              <NotificationDropdown
                notificationList={notificationList}
                unreadCount={unreadCount}
                hasMoreNotifications={hasMoreNotifications}
                isFetchingNotifications={isFetchingNotifications}
                isMarkingAllAsRead={isMarkingAllAsRead}
                onOpenNotifications={handleOpenNotifications}
                onClickNotification={handleClickNotification}
                onLoadMoreNotifications={handleLoadMoreNotifications}
                onMarkAllAsRead={handleMarkAllAsRead}
              />
            )}
            trigger={["click"]}
            placement="bottomRight"
            arrow
            open={isNotificationOpen}
            onOpenChange={(open: boolean) => {
              setIsNotificationOpen(open);
              if (open) {
                setHoveredSetting(false);
              }
            }}
          >
            <div className="relative flex h-[46px] w-[46px] cursor-pointer items-center justify-center rounded-full bg-white/10 backdrop-blur-sm transition-all duration-200 hover:bg-white/15">
              <Badge
                count={unreadCount > 99 ? "99+" : unreadCount}
                size="small"
                offset={[-2, 2]}
                styles={{
                  indicator: {
                    boxShadow: "none",
                    fontSize: 10,
                    minWidth: 18,
                    height: 18,
                    lineHeight: "18px",
                  },
                }}
              >
                <Bell size={22} color="var(--textWhite)" weight="duotone" />
              </Badge>
            </div>
          </Dropdown>

          <div
            ref={settingRef}
            className="relative"
            onMouseEnter={handleSettingMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            <div className="flex h-[46px] w-[46px] cursor-pointer items-center justify-center rounded-full bg-white/10 transition-all duration-200 hover:bg-white/15">
              <Warehouse size={22} color="var(--textWhite)" weight="duotone" />
            </div>

            <AnimatePresence mode="wait">
              {hoveredSetting && sidebarItems && (
                <AnimationCommon
                  key="settings-submenu"
                  effect="fadeDown"
                  transition="spring"
                  disablePresence={true}
                  duration={0.3}
                  className="!absolute top-[64px] z-50 -translate-x-1/2 transform !leading-[20px]"
                  style={{ minWidth: "200px" }}
                >
                  <SubMenu
                    menuItem={{
                      key: "quanLyCaiDat",
                      label: "Quản Lý Cài Đặt",
                      path: "/quan-ly-cai-dat",
                      icon: <Warehouse size={20} />,
                      children: [sidebarItems],
                    }}
                    isVisible={hoveredSetting}
                  />
                </AnimationCommon>
              )}
            </AnimatePresence>
          </div>

          <Dropdown menu={{ items }} placement="bottomLeft" arrow>
            <div className="flex cursor-pointer items-center">
              <div className="flex h-[40px] w-[40px] items-center justify-center">
                <User size={18} color="var(--textWhite)" />
              </div>
              <span style={{ color: "white" }}>
                {profile?.data?.user?.name ?? "ADMIN"}
              </span>
            </div>
          </Dropdown>
        </div>
      </Header>

      <AnimatePresence mode="wait">
        {hoveredMenuItem &&
          hoveredMenuItem.children &&
          hoveredMenuItem.children.length > 0 &&
          hoveredMenuKey &&
          submenuPosition && (
            <AnimationCommon
              key={hoveredMenuKey}
              effect="fadeDown"
              transition="spring"
              duration={0.3}
              disablePresence={true}
              className="absolute top-[64px] z-50"
              style={{
                left: `${submenuPosition.left}px`,
                width: `${submenuPosition.width}px`,
              }}
            >
              <SubMenu
                menuItem={hoveredMenuItem}
                isVisible={hoveredMenuKey === hoveredMenuItem.key}
              />
            </AnimationCommon>
          )}
      </AnimatePresence>

      <Content>
        <Breadcrumb
          menuItems={[...menuItems, sidebarItems, ...sidebarItemsConst]}
        />
      </Content>
    </div>
  );
};

export default Navbar;
